import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import type { ErrorApi } from '../api/errores'
import type { Integraciones } from '../api/modelos'

/**
 * Perfil 360 de un cliente (`GET /api/clientes/:id/perfil`), ya con los nombres
 * de la interfaz que pone `enlace.ts` (`direccion`, `etiquetas`, `estadoCiclo`).
 * El historial sale del puerto interno de albaranes del módulo: funciona sin
 * ningún servicio externo.
 */
export interface PerfilCliente {
  contactId: string
  nombre: string
  telefono: string | null
  direccion: string | null
  codigoPostal: string | null
  poblacion: string | null
  provincia: string | null
  latitud: number | null
  longitud: number | null
  etiquetas: string[]
  estadoCiclo: string | null
  aplazamientos: number
  activo: boolean
  ultimosRepartos: string[]
  albaranes: AlbaranDetalle[]
  /** Resultado del ciclo de esta semana; null si todavía no ha respondido. */
  respuestaSemana: string | null
  respondioSemana: boolean
  /** Teléfono en dígitos, para abrir su conversación. */
  enlaceChat: string | null
}

export interface LineaAlbaran {
  nombre: string
  unidades: number
  precio: number
}

export interface AlbaranDetalle {
  fecha: string
  lineas: LineaAlbaran[]
  total: number
  /** Número interno (ALB-AAAA-NNNNN). */
  numero?: string
}

export interface NotaCliente {
  id: number
  texto: string
  titulo?: string | null
  autor?: string | null
  creadaEn: string
}

/** Tamaño de cada tanda del historial: el backend devuelve 5 por llamada. */
export const ALBARANES_POR_TANDA = 5

const clavePerfil = (contactId: string | null) => ['perfil-cliente', contactId]
const claveNotas = (contactId: string | null) => ['notas-cliente', contactId]

/** Perfil por id. Con el id a null no carga nada (el panel está cerrado). */
export function usePerfilCliente(contactId: string | null) {
  const api = useApi()
  return useQuery<PerfilCliente, ErrorApi>({
    queryKey: clavePerfil(contactId),
    enabled: Boolean(contactId),
    queryFn: ({ signal }) =>
      api.pedir<PerfilCliente>(`/api/clientes/${encodeURIComponent(contactId as string)}/perfil`, { senal: signal }),
    staleTime: 60_000,
  })
}

/** Perfil por teléfono: desde una conversación se conoce el número, no el id. */
export function usePerfilPorTelefono(telefono: string | null) {
  const api = useApi()
  return useQuery<PerfilCliente, ErrorApi>({
    queryKey: ['perfil-cliente-telefono', telefono],
    enabled: Boolean(telefono),
    queryFn: ({ signal }) =>
      api.pedir<PerfilCliente>(`/api/clientes/perfil/por-telefono/${encodeURIComponent(telefono as string)}`, {
        senal: signal,
      }),
    staleTime: 60_000,
  })
}

/**
 * Más albaranes bajo demanda («Cargar más»). Es una lectura, pero se lanza a
 * mano y se acumula en el panel: por eso va como mutación y no como consulta.
 */
export function useCargarAlbaranes(contactId: string) {
  const api = useApi()
  return useMutation<{ items: AlbaranDetalle[]; offset: number }, ErrorApi, { offset: number }>({
    mutationFn: ({ offset }) =>
      api.pedir(`/api/clientes/${encodeURIComponent(contactId)}/albaranes`, { parametros: { offset } }),
  })
}

/** Notas internas del cliente (tabla propia del módulo). */
export function useNotasCliente(contactId: string | null) {
  const api = useApi()
  return useQuery<{ items: NotaCliente[]; total: number }, ErrorApi>({
    queryKey: claveNotas(contactId),
    enabled: Boolean(contactId),
    queryFn: ({ signal }) =>
      api.pedir(`/api/clientes/${encodeURIComponent(contactId as string)}/notas`, { senal: signal }),
  })
}

export function useCrearNota(contactId: string) {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<NotaCliente, ErrorApi, { texto: string }>({
    mutationFn: (cuerpo) =>
      api.pedir<NotaCliente>(`/api/clientes/${encodeURIComponent(contactId)}/notas`, { metodo: 'POST', cuerpo }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: claveNotas(contactId) }),
  })
}

export function useBorrarNota(contactId: string) {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<unknown, ErrorApi, { notaId: number }>({
    mutationFn: ({ notaId }) =>
      api.pedir(`/api/clientes/${encodeURIComponent(contactId)}/notas/${notaId}`, { metodo: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: claveNotas(contactId) }),
  })
}

/**
 * ¿Hay conversaciones que enseñar? Las escribe el asistente de WhatsApp, que
 * solo existe con el bot conectado o con un proveedor de WhatsApp que no sea el
 * modo manual. Sin eso, «Ver su chat» llevaría a una bandeja vacía.
 */
export function hayChatDelBot(integraciones: Integraciones | null | undefined): boolean {
  if (!integraciones) return false
  if (integraciones.n8n?.configurado) return true
  const whatsapp = integraciones.whatsapp
  return Boolean(whatsapp?.configurado && whatsapp.modo !== 'manual')
}
